import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, Lock, Zap, Sparkles, Info } from 'lucide-react';
import { Button } from './ui/button';
import { useCurrency } from '../contexts/CurrencyContext';
import { formatMoney } from '../lib/currency';
import api from '../lib/api';

/**
 * Unified pricing cards used by both the public landing page and the
 * authenticated subscription dashboard. Single source of truth for layout,
 * spacing, badges and responsive grid.
 *
 * Permission model (matches backend `BILLING_ROLES = (admin, manager,
 * billing_admin)`):
 *   - `canManageBilling=true`  → CTAs visible & active.
 *   - `canManageBilling=false` → CTAs hidden, replaced by a localised
 *     notice instructing to contact a manager / admin (subscription
 *     variant). Landing variant simply hides CTAs.
 *
 * Props:
 *   - variant: 'landing' (CTA → /register) | 'subscription' (CTA → onUpgrade)
 *   - plansData: { free, pro, enterprise } from backend or undefined.
 *   - currentPlan, processingPlan, onUpgrade — subscription-only.
 *   - canManageBilling: bool — whether the current user is allowed to
 *     trigger billing actions (default: true).
 */
const PLAN_ORDER = ['free', 'pro', 'enterprise'];

const FALLBACK_PRICES = { free: 0, pro: 49, enterprise: 199 };
const FALLBACK_MAX_CVS = { free: 5, pro: 200, enterprise: 1500 };
const FALLBACK_MAX_USERS = { free: 1, pro: 3, enterprise: 10 };
const FALLBACK_MONTHLY_RESET = { free: false, pro: true, enterprise: true };
// Fallback feature keys used only if /api/pricing/plans is unreachable.
// Kept in-sync with backend/config.py `SUBSCRIPTION_PLANS[*].feature_keys`.
const FALLBACK_FEATURE_KEYS = {
    free: ['ai_basic', 'standard_support'],
    pro: ['ai_advanced', 'smart_matching', 'recommendations', 'pdf_export', 'email_support'],
    enterprise: ['ai_advanced', 'advanced_scoring', 'recommendations', 'pdf_export', 'api_access', 'priority_support', 'white_label'],
};

const formatQuota = (n, locale) => {
    try {
        return new Intl.NumberFormat(locale || 'fr-FR').format(Number(n) || 0);
    } catch {
        return String(n);
    }
};

export default function PricingCards({
    variant = 'landing',
    plansData,
    currentPlan,
    processingPlan,
    onUpgrade,
    canManageBilling = true,
    onPurchaseOverage,
    processingPack,
}) {
    const { t } = useTranslation();
    const { currency, currencyMeta } = useCurrency();

    // Internationalised pricing: fetched from GET /api/pricing/plans?currency=…
    const [intlPlans, setIntlPlans] = useState(null);
    const [intlPacks, setIntlPacks] = useState(null);
    const [indicative, setIndicative] = useState(false);
    useEffect(() => {
        let cancelled = false;
        api.get('/pricing/plans', { params: { currency } })
            .then(res => {
                if (cancelled) return;
                setIntlPlans(res.data?.plans || null);
                setIntlPacks(res.data?.overage_packs || null);
                const paidPlans = Object.entries(res.data?.plans || {}).filter(([id]) => id !== 'free');
                setIndicative(paidPlans.some(([, p]) => p && p.supported_by_stripe === false));
            })
            .catch(() => {
                setIntlPlans(null);
                setIntlPacks(null);
                setIndicative(false);
            });
        return () => { cancelled = true; };
    }, [currency]);

    return (
        <>
            {/* Permission notice: shown ONCE above the cards on the
                subscription variant when the user can't change the plan. */}
            {variant === 'subscription' && !canManageBilling && (
                <div
                    data-testid="pricing-recruiter-notice"
                    className="mb-6 max-w-3xl mx-auto bg-[#FEF3C7] border border-[#F59E0B]/40 text-[#78350F] px-4 py-3 rounded-lg flex items-start gap-3"
                >
                    <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#92400E]" />
                    <p className="text-sm">{t('pricing.recruiter_notice')}</p>
                </div>
            )}

            {/* Cards grid: 1 col mobile → 2 cols tablet → 3 cols desktop */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
                {PLAN_ORDER.map((planId) => {
                    // Intl priority chain: /pricing/plans → plansData → FALLBACK
                    const intl = intlPlans?.[planId];
                    const plan = plansData?.[planId] || {};
                    const price = intl?.price ?? plan.price ?? FALLBACK_PRICES[planId];
                    const planCurrency = intl?.currency || 'eur';
                    const maxCvs = intl?.max_cvs ?? plan.max_cvs ?? FALLBACK_MAX_CVS[planId];
                    const maxUsers = intl?.max_users ?? plan.max_users ?? FALLBACK_MAX_USERS[planId];
                    const monthlyReset = intl?.monthly_reset ?? plan.monthly_reset ?? FALLBACK_MONTHLY_RESET[planId];
                    // Feature bullets: DERIVED EXCLUSIVELY from backend `feature_keys`
                    // (SSOT: /api/pricing/plans). i18n only provides labels.
                    const featureKeys = intl?.feature_keys ?? plan.feature_keys ?? FALLBACK_FEATURE_KEYS[planId];
                    const isPopular = planId === 'pro';
                    const isCurrent = variant === 'subscription' && currentPlan === planId;

                    return (
                        <div
                            key={planId}
                            data-testid={`plan-card-${planId}`}
                            className={`
                                relative bg-white rounded-xl p-6 sm:p-8 flex flex-col
                                transition-all duration-200
                                ${isPopular
                                    ? 'border-2 border-[#002FA7] shadow-xl lg:scale-105 lg:-my-2 lg:z-10'
                                    : isCurrent
                                        ? 'border-2 border-[#10B981] shadow-md'
                                        : 'border border-[#E5E7EB] shadow-sm'
                                }
                            `}
                        >
                            {/* Top badge */}
                            {isCurrent ? (
                                <span
                                    data-testid={`plan-badge-current-${planId}`}
                                    className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#10B981] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md"
                                >
                                    {t('subscription.current_plan_badge')}
                                </span>
                            ) : isPopular ? (
                                <span
                                    data-testid={`plan-badge-popular-${planId}`}
                                    className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#002FA7] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md flex items-center gap-1"
                                >
                                    <Sparkles className="w-3 h-3" />
                                    {t('pricing.popular_badge')}
                                </span>
                            ) : null}

                            {/* Header */}
                            <div className="text-center mb-6">
                                <h3 className="text-xl font-semibold text-[#0A0A0A] font-['Outfit']">
                                    {t(`pricing.${planId}_name`)}
                                </h3>
                                <p className="text-[#9CA3AF] text-sm mt-1 min-h-[1.25rem]">
                                    {t(`pricing.${planId}_tagline`)}
                                </p>
                                <div className="mt-4 flex items-end justify-center gap-1">
                                    <span className="text-4xl sm:text-5xl font-bold text-[#0A0A0A] font-['Outfit']">
                                        {price === 0 ? t('pricing.free_label', { defaultValue: 'Free' }) : formatMoney(price, planCurrency, currencyMeta)}
                                    </span>
                                    {price > 0 && (
                                        <span className="text-[#9CA3AF] text-sm pb-2">
                                            {t('pricing.per_month')}
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-[#6B7280] mt-3">
                                    {monthlyReset
                                        ? t('pricing.analyses_per_month', { max: formatQuota(maxCvs, currencyMeta?.locale) })
                                        : t('pricing.analyses_lifetime', { max: formatQuota(maxCvs, currencyMeta?.locale) })}
                                    {' · '}
                                    {maxUsers <= 1
                                        ? t('pricing.users_one', { count: maxUsers })
                                        : t('pricing.users_other', { count: maxUsers })}
                                </p>
                            </div>

                            {/* Features list — sourced from backend `feature_keys`; i18n only supplies labels. */}
                            <ul className="space-y-3 mb-6 flex-1">
                                {(featureKeys || []).map((key) => (
                                    <li
                                        key={key}
                                        data-testid={`plan-feature-${planId}-${key}`}
                                        className="flex items-start gap-2 text-[#4B5563] text-sm"
                                    >
                                        <CheckCircle className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                                        <span>{t(`pricing.features.${key}`)}</span>
                                    </li>
                                ))}
                            </ul>

                            {/* CTA — gated by canManageBilling */}
                            <PlanCta
                                planId={planId}
                                variant={variant}
                                isPopular={isPopular}
                                isCurrent={isCurrent}
                                processingPlan={processingPlan}
                                onUpgrade={onUpgrade}
                                canManageBilling={canManageBilling}
                            />
                        </div>
                    );
                })}
            </div>

            {/* "Need more analyses?" overage panel */}
            <div id="overage" className="mt-12 max-w-5xl mx-auto bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-6 sm:p-8" data-testid="pricing-overage-panel">
                <h3 className="text-lg sm:text-xl font-semibold text-[#0A0A0A] font-['Outfit'] text-center">
                    {t('pricing.overage_title')}
                </h3>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { id: 'pack_50',   extra: 50,   priceEur: 15 },
                        { id: 'pack_100',  extra: 100,  priceEur: 27 },
                        { id: 'pack_500',  extra: 500,  priceEur: 95 },
                        { id: 'pack_1000', extra: 1000, priceEur: 170 },
                    ].map((pack) => {
                        // Intl priority chain
                        const intlPack = (intlPacks || []).find(p => p.id === pack.id);
                        const price = intlPack?.price ?? pack.priceEur;
                        const packCurrency = intlPack?.currency || 'eur';
                        // Free plan is not eligible for overage packs. On the
                        // subscription screen we lock the cards visually; the
                        // backend enforces the same rule (HTTP 403) to prevent
                        // any API bypass.
                        const isFreePlanLocked =
                            variant === 'subscription' && currentPlan === 'free';
                        const purchasable =
                            variant === 'subscription' &&
                            canManageBilling &&
                            !isFreePlanLocked &&
                            typeof onPurchaseOverage === 'function';
                        const isProcessing = processingPack === pack.id;
                        const baseClass =
                            'bg-white border border-[#E5E7EB] rounded-lg p-4 text-center transition-colors';

                        // Free plan: locked card with upgrade CTA (visible only
                        // on the subscription dashboard — landing page keeps
                        // the simple info-only variant for everyone).
                        if (isFreePlanLocked) {
                            return (
                                <div
                                    key={pack.id}
                                    data-testid={`overage-pack-${pack.extra}`}
                                    className={`${baseClass} opacity-90 hover:border-[#E5E7EB]`}
                                >
                                    <p className="text-xs uppercase tracking-wide text-[#6B7280]">
                                        {t('pricing.overage_extra', { count: pack.extra })}
                                    </p>
                                    <p className="mt-2 text-2xl font-bold text-[#002FA7] font-['Outfit']">
                                        {formatMoney(price, packCurrency, currencyMeta)}
                                    </p>
                                    <p
                                        data-testid={`overage-pack-${pack.extra}-locked-notice`}
                                        className="mt-3 inline-flex items-center justify-center gap-1.5 text-[11px] font-medium text-[#78350F] bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-full px-2.5 py-1"
                                    >
                                        <Lock className="w-3 h-3" />
                                        <span>{t('pricing.overage_free_locked')}</span>
                                    </p>
                                </div>
                            );
                        }

                        if (!purchasable) {
                            return (
                                <div
                                    key={pack.id}
                                    data-testid={`overage-pack-${pack.extra}`}
                                    className={`${baseClass} hover:border-[#002FA7]`}
                                >
                                    <p className="text-xs uppercase tracking-wide text-[#6B7280]">
                                        {t('pricing.overage_extra', { count: pack.extra })}
                                    </p>
                                    <p className="mt-2 text-2xl font-bold text-[#002FA7] font-['Outfit']">
                                        {formatMoney(price, packCurrency, currencyMeta)}
                                    </p>
                                </div>
                            );
                        }
                        return (
                            <button
                                key={pack.id}
                                type="button"
                                data-testid={`overage-pack-${pack.extra}`}
                                onClick={() => onPurchaseOverage(pack.id)}
                                disabled={isProcessing || Boolean(processingPack)}
                                className={`${baseClass} hover:border-[#002FA7] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#002FA7] disabled:opacity-60 disabled:cursor-not-allowed`}
                            >
                                <p className="text-xs uppercase tracking-wide text-[#6B7280]">
                                    {t('pricing.overage_extra', { count: pack.extra })}
                                </p>
                                <p className="mt-2 text-2xl font-bold text-[#002FA7] font-['Outfit']">
                                    {formatMoney(price, packCurrency, currencyMeta)}
                                </p>
                                <span
                                    className="mt-3 inline-flex items-center justify-center gap-1 text-xs font-semibold text-[#002FA7]"
                                    data-testid={`overage-buy-cta-${pack.extra}`}
                                >
                                    {isProcessing ? (
                                        <>
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                            {t('subscription.verifying')}
                                        </>
                                    ) : (
                                        <>
                                            <Zap className="w-3 h-3" />
                                            {t('pricing.overage_buy_cta')}
                                        </>
                                    )}
                                </span>
                            </button>
                        );
                    })}
                </div>
                <p className="mt-4 text-xs text-[#6B7280] text-center">
                    {t('pricing.overage_note')}
                </p>
                {indicative && (
                    <p
                        data-testid="pricing-indicative-notice"
                        className="mt-2 text-[11px] text-[#92400E] text-center italic bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-md px-3 py-2 max-w-md mx-auto"
                    >
                        {t('currency.indicative_notice', {
                            defaultValue: 'Indicative price — billing will be processed in EUR via our secure payment processor.',
                            currency: (currency || 'eur').toUpperCase(),
                        })}
                    </p>
                )}
                {variant === 'subscription' && !canManageBilling && (
                    <p
                        data-testid="overage-recruiter-notice"
                        className="mt-2 text-[11px] text-[#92400E] text-center italic"
                    >
                        {t('pricing.overage_recruiter_notice')}
                    </p>
                )}
            </div>
        </>
    );
}

function PlanCta({ planId, variant, isPopular, isCurrent, processingPlan, onUpgrade, canManageBilling }) {
    const { t } = useTranslation();
    const baseCtaClass = isPopular
        ? 'bg-[#002FA7] hover:bg-[#002480] text-white shadow-md'
        : 'bg-white border border-[#E5E7EB] text-[#0A0A0A] hover:bg-[#F9FAFB]';

    if (variant === 'landing') {
        // Recruiters logged in shouldn't be encouraged to register again
        // or to start an upgrade flow they can't complete.
        if (!canManageBilling) {
            return null;
        }
        return (
            <Link to="/register" data-testid={`plan-cta-${planId}`}>
                <Button className={`w-full ${baseCtaClass}`}>
                    {t(`pricing.landing_cta_${planId}`)}
                </Button>
            </Link>
        );
    }

    // subscription variant
    if (isCurrent) {
        return (
            <Button disabled className="w-full" variant="outline" data-testid={`plan-cta-${planId}`}>
                {t('pricing.current_plan_cta')}
            </Button>
        );
    }

    if (!canManageBilling) {
        // No CTA button. The page-level notice already explains why.
        return null;
    }

    const isProcessing = processingPlan === planId;
    return (
        <Button
            data-testid={`plan-cta-${planId}`}
            className={`w-full ${baseCtaClass}`}
            onClick={() => onUpgrade?.(planId)}
            disabled={isProcessing}
        >
            {isProcessing ? (
                <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('subscription.verifying')}
                </>
            ) : (
                <>
                    <Zap className="w-4 h-4 mr-2" />
                    {t(`pricing.subscription_cta_${planId}`)}
                </>
            )}
        </Button>
    );
}
